// Section 22 Build tab: buildings owned with condition bars, effect strength (explain() of the
// buildingEffect target), upkeep due and a repair button. Props only.
import type { BuildingId } from '../../sim/api';
import type { BuildingRow } from '../../viewModels/build';
import { buildText } from '../text/en';
import { Explain } from './explain';
import { materialsText } from './materials';

interface Props {
  rows: readonly BuildingRow[];
  canEdit: boolean;
  onRepair: (id: BuildingId) => void;
}

export function OwnedBuildings({ rows, canEdit, onRepair }: Props) {
  if (rows.length === 0) return <p class="hint">{buildText.noBuildings}</p>;
  return (
    <table class="table build__owned">
      <thead>
        <tr>
          <th scope="col">{buildText.colBuilding}</th>
          <th scope="col">{buildText.colCondition}</th>
          <th scope="col">{buildText.colEffect}</th>
          <th scope="col">{buildText.colUpkeep}</th>
          <th scope="col">{buildText.colActions}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((b) => (
          <tr key={b.id} class={b.condition < 50 ? 'is-worn' : undefined}>
            <th scope="row">
              {b.label} <span class="hint num">{b.id}</span>
              {b.frozen && <span class="build__flag"> ({buildText.frozen})</span>}
            </th>
            <td>
              <meter
                class="build__meter"
                min={0}
                max={100}
                low={50}
                optimum={100}
                value={b.condition}
                aria-label={`${b.label} ${buildText.colCondition}`}
              />{' '}
              <span class="num">{Math.round(b.condition)}</span>
            </td>
            <td>
              <Explain
                value={`${Math.round(b.effect * 100)}%`}
                factors={b.effectFactors}
                label={`${b.label} ${buildText.colEffect}`}
              />
            </td>
            <td>
              {buildText.upkeepIn(b.upkeepIn)}: <span class="num">{materialsText(b.upkeep)}</span>
              {b.upkeepIn <= 0 && !b.upkeepAffordable && (
                <strong class="build__short"> {buildText.upkeepShort}</strong>
              )}
            </td>
            <td>
              {b.repairQueued ? (
                <span class="hint">{buildText.repairQueued}</span>
              ) : b.canRepair ? (
                <button
                  type="button"
                  class="button button--small"
                  disabled={!canEdit}
                  onClick={() => onRepair(b.id)}
                >
                  {buildText.repair}
                </button>
              ) : (
                <span class="hint">{buildText.fullCondition}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
